(ns ui.data.view-entities.event
  (:require
   [frontend.updates.entity-scope :as entity-scope]
   [re-frame.core :as rf]))

(def root-path [:ui.data/views])

(defn- valid-view-id?
  [view-id]
  (and (vector? view-id)
       (seq view-id)
       (every? keyword? view-id)))

(defn- scope-path
  [view-id scope-id]
  (into root-path (concat view-id [:scopes scope-id])))

(defn- status-path
  [view-id scope-id]
  (conj (scope-path view-id scope-id) :status))

(defn- config-path
  [view-id scope-id]
  (conj (scope-path view-id scope-id) :config))

(defn- stream-token-path
  [view-id scope-id]
  (conj (config-path view-id scope-id) :stream-token))

(defn- entities-by-id-path
  [view-id scope-id]
  (conj (scope-path view-id scope-id) :entities/by-id))

(defn- meta-path
  [view-id scope-id]
  (conj (scope-path view-id scope-id) :meta))

(defn- unwrap-body
  [payload]
  (if (and (map? payload)
           (map? (:body payload)))
    (:body payload)
    payload))

(defn- message->ops
  [message]
  (if (map? message)
    (or (:ops message) [])
    []))

(defn- upsert-entities
  [ops]
  (->> ops
       (filter (fn [op]
                 (= :upsert (:op op))))
       (mapcat (fn [op]
                 (or (:entities op) [])))
       (remove nil?)
       (vec)))

(defn- removed-entity-ids
  [ops]
  (->> ops
       (filter (fn [op]
                 (= :remove (:op op))))
       (mapcat (fn [op]
                 (or (:entity-ids op) [])))
       (filter int?)
       (distinct)
       (vec)))

(rf/reg-event-fx
 ::open-view-entity-scope
 (fn [{:keys [db]} [_ {:keys [view-id scope-id entity-id instance-id]
                       :or   {scope-id :entity-scope}
                       :as   opts}]]
   (let [valid? (valid-view-id? view-id)
         stream-token (str (random-uuid))
         db' (if valid?
               (-> db
                   (assoc-in (config-path view-id scope-id)
                             {:entity-id entity-id
                              :instance-id instance-id
                              :view-id view-id
                              :scope-id scope-id
                              :stream-token stream-token})
                   (assoc-in (status-path view-id scope-id)
                             {:loading? true
                              :error nil}))
               (assoc-in db (status-path (or view-id [:invalid]) scope-id)
                         {:loading? false
                          :error "Invalid view-id. Expected a non-empty vector of keywords."}))]
     (if (and valid? (int? entity-id))
       (do
         (entity-scope/open!
          {:entity-id entity-id
           :instance-id instance-id
           :on-message (fn [message]
                         (rf/dispatch [::stream-message
                                       {:view-id view-id
                                        :scope-id scope-id
                                        :stream-token stream-token
                                        :message message}]))
           :on-error (fn [error]
                       (rf/dispatch [::stream-failed
                                     {:view-id view-id
                                      :scope-id scope-id
                                      :stream-token stream-token
                                      :error error}]))
           :on-open (fn [response]
                      (rf/dispatch [::stream-opened
                                    {:view-id view-id
                                     :scope-id scope-id
                                     :stream-token stream-token
                                     :response response}]))})
         {:db db'})
       (if valid?
         {:db (assoc-in db' (status-path view-id scope-id)
                        {:loading? false
                         :error "Invalid entity-id for entity scope subscription."})}
         {:db db'})))))

(rf/reg-event-fx
 ::close-view-entity-scope
 (fn [{:keys [db]} [_ {:keys [view-id scope-id entity-id instance-id]
                       :or {scope-id :entity-scope}}]]
   (when (int? entity-id)
     (entity-scope/close! entity-id instance-id))
   {:db (if (valid-view-id? view-id)
          (assoc-in db (scope-path view-id scope-id) nil)
          db)}))

(rf/reg-event-fx
 ::stream-opened
 (fn [{:keys [db]} [_ {:keys [view-id scope-id stream-token response]}]]
   (if (valid-view-id? view-id)
     (let [active-token (get-in db (stream-token-path view-id scope-id))]
       (if (= stream-token active-token)
         (let [response (unwrap-body response)
               initial-message (:initial-message response)]
           (cond-> {:db (update-in db (status-path view-id scope-id)
                                   (fn [status]
                                     (assoc (or status {:loading? true
                                                        :error nil})
                                            :error nil)))}
             (map? initial-message)
             (assoc :dispatch [::stream-message
                               {:view-id view-id
                                :scope-id scope-id
                                :stream-token stream-token
                                :message initial-message}])))
         {:db db}))
     {:db db})))

(rf/reg-event-fx
 ::stream-message
 (fn [{:keys [db]} [_ {:keys [view-id scope-id stream-token message]}]]
   (if (valid-view-id? view-id)
     (let [active-token (get-in db (stream-token-path view-id scope-id))]
       (if (= stream-token active-token)
         (let [message (unwrap-body message)
               ops (message->ops message)
               upserts (upsert-entities ops)
               removed-ids (removed-entity-ids ops)
               upserts-by-id (->> upserts
                                  (keep (fn [entity]
                                          (when-let [entity-id (:db/id entity)]
                                            [entity-id entity])))
                                  (into {}))
               prev-entities (or (get-in db (entities-by-id-path view-id scope-id)) {})
               merged-entities (merge prev-entities upserts-by-id)
               next-entities (apply dissoc merged-entities removed-ids)
               root-entity-id (get-in db (conj (config-path view-id scope-id) :entity-id))
               removed-root? (some #{root-entity-id} removed-ids)
               root-entity (get next-entities root-entity-id)
               initial-message? (= :initial (:type message))
               error (cond
                       removed-root? "Entity was removed."
                       (and initial-message? (int? root-entity-id) (nil? root-entity))
                       "Unable to load entity data from subscription."
                       :else nil)]
           {:db (-> db
                    (assoc-in (entities-by-id-path view-id scope-id) next-entities)
                    (assoc-in (meta-path view-id scope-id)
                              {:basis-t (:basis-t message)
                               :type (:type message)})
                    (assoc-in (status-path view-id scope-id)
                              {:loading? false
                               :error error}))})
         {:db db}))
     {:db db})))

(rf/reg-event-db
 ::stream-failed
 (fn [db [_ {:keys [view-id scope-id stream-token error]}]]
   (if (valid-view-id? view-id)
     (let [active-token (get-in db (stream-token-path view-id scope-id))]
       (if (= stream-token active-token)
         (assoc-in db (status-path view-id scope-id)
                   {:loading? false
                    :error (or (some-> error :message)
                               (when (string? error) error)
                               (pr-str error))})
         db))
     db)))
